import { importLibrary } from "@googlemaps/js-api-loader";
import { KEYWORD_SEARCH_ZOOM, KEYWORD_SEARCH_BOUNDS } from "./consts.js";

/**
 * キーワード（駅/住所/施設）で地図を移動させる機能を配線する
 *
 * Geocoding API でキーワードを座標に変換し、地図をその地点へ移動する。
 * 移動後は map の idle イベントが発火するため、マーカーの再取得は既存の refresh に任せる。
 *
 * @param {Object} params
 * @param {google.maps.Map} params.map - 対象の地図
 * @param {import("./dom.js").MapPageElements} params.els - ページの DOM 参照
 * @returns {void}
 */
export const setupKeywordSearch = ({ map, els }) => {
    const { keywordInput, keywordSubmit } = els;
    if (!keywordInput) return;

    /** @type {google.maps.Geocoder|null} 初回検索時に生成して使い回す */
    let geocoder = null;
    let isSearching = false;

    /**
     * 入力されたキーワードで地図を移動する
     * @returns {Promise<void>}
     */
    const search = async () => {
        const keyword = keywordInput.value.trim();
        if (!keyword || isSearching) return;

        isSearching = true;
        try {
            if (!geocoder) {
                const { Geocoder } = await importLibrary("geocoding");
                geocoder = new Geocoder();
            }

            const { results } = await geocoder.geocode({
                address: keyword,
                region: "JP",
                // 同名地名が全国にある場合に東北の結果を優先する
                bounds: KEYWORD_SEARCH_BOUNDS,
            });

            const place = results?.[0];
            if (!place) {
                alert("該当する場所が見つかりませんでした");
                return;
            }

            // 施設や地域は viewport が返るためそれに合わせる（駅や番地は viewport が狭いので妥当）
            const viewport = place.geometry?.viewport;
            if (viewport) {
                map.fitBounds(viewport);
            } else {
                map.panTo(place.geometry.location);
                map.setZoom(KEYWORD_SEARCH_ZOOM);
            }
        } catch (error) {
            // ZERO_RESULTS は正常系（該当なし）として扱う
            if (error?.code === "ZERO_RESULTS") {
                alert("該当する場所が見つかりませんでした");
                return;
            }
            console.error("キーワード検索に失敗しました:", error);
            alert("検索に失敗しました。時間をおいて再度お試しください");
        } finally {
            isSearching = false;
        }
    };

    keywordSubmit?.addEventListener("click", search);
    // Enter キーでも検索できるようにする（フォーム送信によるリロードは抑止）
    keywordInput.addEventListener("keydown", (event) => {
        if (event.key !== "Enter") return;
        event.preventDefault();
        search();
    });
};
